import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  HERSTEL_PAD,
  mag,
  UITNODIGING_PAD,
  WACHTWOORD_VERGETEN_PAD,
  type Recht,
} from '@vip/shared';
import { useAuth } from './features/auth/useAuth';
import { LoginPage } from './features/auth/LoginPage';
import { AccountAanmakenPage } from './features/auth/AccountAanmakenPage';
import { WachtwoordVergetenPage } from './features/auth/WachtwoordVergetenPage';
import { WachtwoordHerstellenPage } from './features/auth/WachtwoordHerstellenPage';
import { Layout } from './components/Layout/Layout';
import { MijnGesprekken } from './features/gesprekken/MijnGesprekken';
import { TranscriptReview } from './features/review/TranscriptReview';
import { HulpvraagReview } from './features/review/HulpvraagReview';
import { InstellingenPagina } from './features/instellingen/InstellingenPagina';
import { OverzichtPagina } from './features/overzicht/OverzichtPagina';
import { HulpvragenPagina } from './features/hulpvragen/HulpvragenPagina';
import { BegeleidingPagina } from './features/begeleiding/BegeleidingPagina';
import { PersonenPagina } from './features/personen/PersonenPagina';
import { OpdrachtPagina } from './features/personen/OpdrachtPagina';
import { PersoonPagina } from './features/personen/PersoonPagina';

/**
 * Route-bewaking. De echte afdwinging staat in de Worker — dit voorkomt alleen
 * dat iemand op een scherm belandt waar de API hem toch weigert. Plan §2.5:
 * de regels horen in de querylaag, niet in de UI.
 */
function Beveiligd({ recht, children }: { recht?: Recht; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (recht && !mag(user.rechten, recht)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Waar iemand landt.
 *
 * Dit stond op de rol ("ben je buddy?") met in het commentaar de aanname dat er
 * geen scherm is dat voor allebei zinvol is. Die aanname vervalt: iemand kan
 * tegelijk Admin en buddy zijn, en ziet dan allebei de schermen.
 *
 * Wie gesprekken aanlevert begint bij zijn eigen gesprekken. Het dashboard
 * stond eerst voorop, maar dat mag inmiddels iedereen zien; zonder deze
 * volgorde landt een buddy op een scherm over de hele club terwijl zijn
 * gesprekken zijn werk zijn. Het dashboard staat één klik verderop.
 *
 * De tak kijkt naar `meeting:create` en niet naar het lezen: dat scherm is
 * inmiddels van iedereen, en wie er niets aanlevert zou anders op een lege
 * lijst landen.
 *
 * De laatste tak is een vangnet tegen een lus: zonder rechten zou een navigatie
 * naar /gesprekken meteen terugkaatsen naar hier, en dan blijft de app rondjes
 * draaien. In de praktijk kan het niet (zonder rol geen inlog), maar een
 * oneindige redirect is een te vervelende manier om daarachter te komen.
 */
function Startpagina() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (mag(user.rechten, 'meeting:create')) return <Navigate to="/gesprekken" replace />;
  if (mag(user.rechten, 'dashboard:read')) return <Navigate to="/overzicht" replace />;
  return (
    <div className="p-8 text-muted-foreground">
      Dit account heeft geen rol met toegang tot een scherm. Vraag een Admin om er een toe te
      kennen.
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Exact het pad uit `uitnodigingsLink` in worker/src/lib/invite.ts, met
          het token als padsegment omdat `AccountAanmakenPage` hem uit
          `useParams` leest. Hier stond `/account-aanmaken` zonder token: dat
          matchte de link uit de mail niet, dus die viel in het vangnet
          onderaan en kwam via `Startpagina` op de inlogpagina uit. */}
      <Route path={UITNODIGING_PAD} element={<AccountAanmakenPage />} />

      {/* Wachtwoord vergeten: het formulier, en het scherm achter de link uit de
          mail. Beide paden komen uit `shared`, net als het uitnodigingspad, zodat
          de link die de worker bouwt en de route hier niet uiteen kunnen lopen. */}
      <Route path={WACHTWOORD_VERGETEN_PAD} element={<WachtwoordVergetenPage />} />
      <Route path={HERSTEL_PAD} element={<WachtwoordHerstellenPage />} />

      {/* Buiten Layout: geen hoofdmenu, eigen tabjes, eigen terugknop. */}
      <Route
        path="/instellingen"
        element={
          <Beveiligd>
            <InstellingenPagina />
          </Beveiligd>
        }
      />
      <Route
        element={
          <Beveiligd>
            <Layout />
          </Beveiligd>
        }
      >
        <Route path="/" element={<Startpagina />} />
        <Route
          path="/gesprekken"
          element={
            <Beveiligd recht="meeting:read:own">
              <MijnGesprekken />
            </Beveiligd>
          }
        />
        <Route
          path="/gesprekken/:id/transcript"
          element={
            <Beveiligd recht="transcript:read:own">
              <TranscriptReview />
            </Beveiligd>
          }
        />
        <Route
          path="/gesprekken/:id/hulpvragen"
          element={
            <Beveiligd recht="keyword:review:own">
              <HulpvraagReview />
            </Beveiligd>
          }
        />
        <Route
          path="/overzicht"
          element={
            <Beveiligd recht="dashboard:read">
              <OverzichtPagina />
            </Beveiligd>
          }
        />
        <Route
          path="/personen"
          element={
            <Beveiligd recht="persoon:read">
              <PersonenPagina />
            </Beveiligd>
          }
        />
        {/* Begeleiding stond als tabblad onder Personen. Het is een eigen taak
            met een eigen scherm geworden: hier hang je mensen aan een buddy, en
            dat gaat over de opdrachten heen. */}
        <Route
          path="/begeleiding"
          element={
            <Beveiligd recht="persoon:read">
              <BegeleidingPagina />
            </Beveiligd>
          }
        />
        {/* `nieuw` staat vóór `:id`, maar react-router kiest sowieso het vaste
            pad boven het variabele. Allebei dezelfde pagina: aanmaken is
            hetzelfde formulier zonder gegevens erin.

            Het vinkje gaat als eigenschap mee en niet via het pad. Een vast pad
            levert geen parameter op, dus de pagina kon `nieuw` er niet uit
            aflezen; hier is het gewoon bekend. */}
        <Route
          path="/personen/nieuw"
          element={
            <Beveiligd recht="persoon:manage">
              <PersoonPagina nieuw />
            </Beveiligd>
          }
        />
        <Route
          path="/personen/:id"
          element={
            <Beveiligd recht="persoon:read">
              <PersoonPagina />
            </Beveiligd>
          }
        />
        <Route
          path="/opdrachten/:id"
          element={
            <Beveiligd recht="persoon:read">
              <OpdrachtPagina />
            </Beveiligd>
          }
        />
        <Route
          path="/hulpvragen"
          element={
            <Beveiligd recht="taxonomie:read">
              <HulpvragenPagina />
            </Beveiligd>
          }
        />
        {/* Vangnet: een onbekend pad hoorde nooit een leeg scherm te geven. */}
        <Route path="*" element={<Startpagina />} />
      </Route>
    </Routes>
  );
}
